// components/Layout.js
import React, { Suspense, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar, { getPageName } from "./Navbar";
import SeasonalBackground from "./SeasonalBackground";
import NavbarConfetti from "./NavbarConfetti";
import PageLoader from "./shared/PageLoader";
import { usePerfMode } from "../utils/perfMode";
import "./Layout.css";
import "./shared/MobilePages.css";

// Pages laid out for a phone screen. The app is rendered at a fixed 1280 px "desktop" width on phones (see the viewport tag
// in public/index.html); these routes switch to the phone's real width instead, with the top bar + drawer menu.
// MOBILE_FRIENDLY_ROUTES also cover their sub-pages; MOBILE_FRIENDLY_PAGES only the page itself (a hub is laid out for
// phones, the pages it links to are not yet).
const MOBILE_FRIENDLY_ROUTES = ['/return', '/billing', '/bill-of-lading', '/weekly-sales-prices', '/packing-list', '/account-statements',
  // Gestión Semanal: the four chart editors with their saved lists, and Cierre Semanal
  '/purchase-chart', '/price-chart', '/cost-chart', '/local-billing', '/weekly-closing',
  // Finanzas: Préstamos Financieros and its Lista
  '/finances/loans',
  // Operaciones: Rastreo, Embarques and its Lista
  '/tracking', '/shipments'];
const MOBILE_FRIENDLY_PAGES = ['/', '/admin', '/accounts', '/reports', '/closing', '/invoicing', '/cost-control', '/inventory',
  '/finances', '/operations', '/apis',
  // Admin
  '/settings', '/companies', '/company-bank-accounts', '/clients', '/payment-conditions', '/conditions', '/product-billing',
  '/product-types', '/box-types', '/producers', '/brands', '/shipment', '/supplier', '/whatsapp-groups', '/users',
  // Reportes
  '/director-dashboard', '/analytics', '/payment-calculator', '/chart-summary',
  // Novedades, Mi perfil, the share chooser
  '/updates', '/profile', '/share'];
export const isMobileFriendly = (pathname) => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return MOBILE_FRIENDLY_PAGES.includes(path) || MOBILE_FRIENDLY_ROUTES.some(r => path === r || path.startsWith(r + '/'));
};

const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [dockSide, setDockSide] = useState(() => localStorage.getItem("sbDockSide") || "left");
  const location = useLocation();
  const perfMode = usePerfMode();                    // "lite" on phones / weak devices: no clouds, confetti or seasonal layers

  useEffect(() => {
    const handle = () => {
      if (window.innerWidth <= 1100) setCollapsed(true);
      else setCollapsed(false);
    };
    handle();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  useEffect(() => {
    document.title = `${getPageName(location.pathname)} · Sunrise Fruit`;
  }, [location.pathname]);

  const mobileFriendly = isMobileFriendly(location.pathname);
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) meta.setAttribute('content', mobileFriendly ? 'width=device-width, initial-scale=1' : 'width=1280');
    // on <body> too: windows opened in a portal (Propiedades, menus) live outside .layout-root (MobilePages.css)
    document.body.classList.toggle('mf-page', mobileFriendly);
    return () => document.body.classList.remove('mf-page');
  }, [mobileFriendly]);

  // Phone cards (MobilePages.css): each cell of a table.m-cards gets data-label = its column header's text, so a row can
  // show "Cliente: …" once the header row is hidden. Re-run on any change in the page (rows load, filters change).
  // Cells that set their own data-label keep it; the Opciones column gets none (its ⋮ button sits top-right).
  // The headers are the header's last row (a band like "Descuentos" above it is not a column), spread over their
  // colSpan; the totals row (tfoot) is labelled the same way.
  useEffect(() => {
    if (!mobileFriendly) return undefined;
    const root = pageRef.current;
    if (!root) return undefined;
    let frame = 0;
    const label = () => {
      frame = 0;
      root.querySelectorAll('table.m-cards').forEach(table => {
        const heads = [...(table.querySelector('thead tr:last-child')?.children || [])]
          .flatMap(th => Array(th.colSpan || 1).fill(th.textContent.trim()));
        if (/^(ID|#)$/i.test(heads[0] || '')) table.dataset.idFirst = '1'; else delete table.dataset.idFirst;
        table.querySelectorAll('tbody tr:not(.empty-row), tfoot tr').forEach(tr => {
          let col = 0;
          [...tr.children].forEach(td => {
            const text = heads[col] || '';
            if (!td.hasAttribute('data-label') || td.dataset.autoLabel) {
              const value = text === 'Opciones' ? '' : text;
              if (td.getAttribute('data-label') !== value) { td.setAttribute('data-label', value); td.dataset.autoLabel = '1'; }
            }
            col += td.colSpan || 1;
          });
        });
      });
    };
    label();
    const mo = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(label); });
    mo.observe(root, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [mobileFriendly, location.pathname]);

  // Restart the page fade-in on navigation (skipping the first run — the
  // initial render already plays it from the CSS class).
  const pageRef = useRef(null);
  const firstRoute = useRef(true);
  useEffect(() => {
    if (firstRoute.current) { firstRoute.current = false; return; }
    const el = pageRef.current;
    if (!el) return;
    el.classList.remove("page-enter");
    void el.offsetWidth; // force reflow so the animation restarts
    el.classList.add("page-enter");
  }, [location.pathname]);

  const handleSetDockSide = (side) => {
    setDockSide(side);
    localStorage.setItem("sbDockSide", side);
  };

  // "left"/"top" — sidebar first (default order). "right"/"bottom" — content
  // comes first instead, so the sidebar visually ends up on the other side.
  const isHorizontal = dockSide === "top" || dockSide === "bottom";
  const contentFirst = dockSide === "right" || dockSide === "bottom";

  return (
    <div className={`layout-root${mobileFriendly ? " mobile-friendly" : ""}`} style={{ flexDirection: isHorizontal ? "column" : "row" }}>
      {perfMode === "full" && <SeasonalBackground />}
      <Navbar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        dockSide={dockSide}
        onSetDockSide={handleSetDockSide}
      />
      <div className="main-wrapper" style={{ order: contentFirst ? -1 : 0 }}>
        <main className="main-content">
          {/* Fades each page in as you open it. The class is on from the first
              render (so the initial load animates) and restarted on route
              changes by the effect above — deliberately NOT a `key` on this
              div, which would remount the page and drop its state whenever
              two routes share a component. */}
          <div ref={pageRef} className="page-enter"><Suspense fallback={<PageLoader message="Cargando…" />}>{children}</Suspense></div>
        </main>
      </div>
      {perfMode === "full" && <NavbarConfetti />}
    </div>
  );
};

export default Layout;
